import React from 'react';
import { Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import { toLocaleFormatSlash } from '../../../../helpers/dateFormat';
import getRoleDisplayName, { truncateText } from '../../../../helpers/getRoleDisplayName';

interface ColumnHeaderConfig {
  orderByColumn: string;
  orderByDesc: boolean;
  setOrderByColumn: (value: string) => void;
  setOrderByDesc: (value: boolean) => void;
}

export interface DirectoryColumnsParams {
  isMobile: boolean;
  actions: (row: any) => React.ReactNode;
  showLoaInfosAlert: (row: any) => void;
  showTerminationInfosAlert: (row: any) => void;
  denyReasonAlert: (reason: string) => void;
  isEducationDirectorAdminAssistant: boolean;
  columnHeaderConfig: ColumnHeaderConfig;
}

export const buildDirectoryColumns = ({
  isMobile,
  actions,
  showLoaInfosAlert,
  showTerminationInfosAlert,
  denyReasonAlert,
  isEducationDirectorAdminAssistant,
  columnHeaderConfig,
}: DirectoryColumnsParams) => {
  const { orderByColumn, orderByDesc, setOrderByColumn, setOrderByDesc } = columnHeaderConfig;

  const columnHeader = (title: string, customOrderingKey: string) => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '5px' }}>{title}</div>
      <div style={{ display: 'flex' }}>
        <i
          style={{
            fontSize: 'small',
            marginLeft: '1px',
            cursor: 'pointer',
            fontWeight: 900,
            color: 'black',
            opacity: orderByColumn === customOrderingKey && !orderByDesc ? '1' : '0.3',
          }}
          className="fas fa-solid fa-arrow-up"
          title={`Sort ascending by ${title}`}
          onClick={() => {
            setOrderByColumn(customOrderingKey);
            setOrderByDesc(false);
          }}
        />
        <i
          style={{
            fontSize: 'small',
            marginLeft: '1px',
            cursor: 'pointer',
            fontWeight: 900,
            color: 'black',
            opacity: orderByColumn === customOrderingKey && orderByDesc ? '1' : '0.3',
          }}
          className="fas fa-solid fa-arrow-down"
          title={`Sort descending by ${title}`}
          onClick={() => {
            setOrderByColumn(customOrderingKey);
            setOrderByDesc(true);
          }}
        />
      </div>
    </div>
  );

  let columns: any[] = [
    {
      id: 'name',
      name: columnHeader('Name', 'custom_ordering_fullName'),
      grow: 3,
      fixed: 'left',
      style: {
        position: 'sticky',
        left: 0,
        backgroundColor: 'white',
        zIndex: 2,
      },
      route: (row: any) => `/users/${row?.user?.id}/profile`,
      cell: (row: any) => (
        <Link to={`/users/${row?.user?.id}/profile`}>
          <div className="d-flex align-items-center">
            <div style={{ width: '35px', height: '35px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
              <img
                src={row.user.picture}
                alt="User Profile"
                className="img-fluid"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <span className="ml-2">{`${row.user.firstName || '-'} ${row.user.lastName || '-'}`}</span>
          </div>
        </Link>
      ),
    },
    {
      name: columnHeader('Status', 'custom_ordering_status'),
      selector: (row: any) => row.terminationDate,
      grow: 2,
      cell: (row: any) => (
        <Badge
          color=""
          className="badge-dot badge-lg"
          style={{ fontSize: '0.8125rem', verticalAlign: 'middle', lineHeight: 'normal' }}
        >
          <>
            {row.status === 'ACTIVE' && <i className="bg-success mr-2" style={{ verticalAlign: 'baseline' }} />}
            {(row.status === 'TERMINATED' || row.status === 'DENIED' || row.status === 'DECLINED') && (
              <i className="bg-danger mr-2" style={{ verticalAlign: 'baseline' }} />
            )}
            {row.status === 'BLOCKED' && <i className="bg-pink mr-2" style={{ verticalAlign: 'baseline' }} />}
            {row.status === 'LOA' && <i className="bg-teal mr-2" style={{ verticalAlign: 'baseline' }} />}
            {row.status === 'OFFER_SENT' && <i className="bg-teal mr-2" style={{ verticalAlign: 'baseline' }} />}
            {row.status === 'HIRED' && <i className="bg-yellow mr-2" style={{ verticalAlign: 'baseline' }} />}
            {row.status === 'PENDING' && <i className="bg-purple mr-2" style={{ verticalAlign: 'baseline' }} />}
            {String(row.status)?.replace('_', ' ')?.toUpperCase()}
            {row.status === 'LOA' && (
              <>
                {' '}
                <i
                  className="fa fa-question-circle cursor-pointer mt-n1"
                  title="View LOA Details"
                  onClick={() => showLoaInfosAlert(row)}
                ></i>
              </>
            )}
            {row.status === 'TERMINATED' && (
              <>
                {' '}
                <i
                  className="fa fa-question-circle cursor-pointer mt-n1"
                  title={row.denyReason}
                  onClick={() => showTerminationInfosAlert(row)}
                ></i>
              </>
            )}
            {row.status === 'DENIED' && row.denyReason && (
              <>
                {' '}
                <i
                  className="fa fa-question-circle cursor-pointer mt-n1"
                  title={row.denyReason}
                  onClick={() => denyReasonAlert(row.denyReason)}
                ></i>
              </>
            )}
            {row.status === 'DECLINED' && row.offerLetterRejectReason && (
              <>
                {' '}
                <i
                  className="fa fa-question-circle cursor-pointer mt-n1"
                  title={row.offerLetterRejectReason}
                  onClick={() => denyReasonAlert(row.offerLetterRejectReason)}
                ></i>
              </>
            )}
          </>
        </Badge>
      ),
    },
  ];

  if (isMobile) {
    columns = [
      ...columns,
      ...[
        {
          name: '',
          grow: 1,
          cell: actions,
          allowOverflow: true,
        },
      ],
    ];
  }

  if (!isMobile) {
    columns = [
      ...columns,
      ...[
        {
          name: columnHeader('Role', 'custom_ordering_role'),
          selector: (row: any) => truncateText(getRoleDisplayName(row?.user?.group?.name), 16),
          grow: 1,
          cell: (row: any) => truncateText(getRoleDisplayName(row?.user?.group?.name), 16),
        },
        {
          name: columnHeader('Type', 'custom_ordering_workday'),
          selector: (row: any) => row.workday,
          grow: 1,
          cell: (row: any) => row.workday,
        },
        {
          name: columnHeader('Title', 'custom_ordering_title'),
          selector: (row: any) => row.title.name,
          grow: 1,
          cell: (row: any) =>
            row?.title?.name ? (
              row?.title?.name?.split(' ').map((item: string) => (
                <>
                  {item}
                  <br />
                </>
              ))
            ) : (
              <>-</>
            ),
        },
        {
          name: columnHeader('Department', 'custom_ordering_department'),
          selector: (row: any) => row.department.name,
          grow: 1,
          cell: (row: any) =>
            row?.department?.name ? (
              row?.department?.name?.split(' ').map((item: string) => (
                <>
                  {item}
                  <br />
                </>
              ))
            ) : (
              <>-</>
            ),
        },
        {
          name: columnHeader('Supervisor', 'custom_ordering_supervisor'),
          selector: (row: any) => row.supervisor.name,
          grow: 1,
          cell: (row: any) =>
            row?.supervisor?.name ? (
              row?.supervisor?.name?.split(' ').map((item: string) => (
                <>
                  {item}
                  <br />
                </>
              ))
            ) : (
              <>-</>
            ),
        },
        {
          name: columnHeader('Start Date', 'custom_ordering_start_date'),
          selector: (row: any) => row.startDate,
          grow: 1,
          cell: (row: any) => toLocaleFormatSlash(row.startDate),
        },
        {
          name: columnHeader('Termination Date', 'custom_ordering_termination_date'),
          selector: (row: any) => row.terminationDate,
          grow: 2,
          cell: (row: any) => (row?.terminationDate ? toLocaleFormatSlash(row.terminationDate) : '-'),
        },
        {
          name: columnHeader('Paperwork', 'custom_ordering_paperwork'),
          selector: (row: any) => row.paperwork,
          grow: 1,
          cell: (row: any) => (
            <i
              className={`fas ${row?.paperwork ? 'fa-check-circle' : 'fa-ban'}`}
              style={{ color: `${row?.paperwork ? 'green' : 'red'}` }}
            />
          ),
        },
        {
          name: '',
          grow: 1,
          cell: actions,
          omit: isEducationDirectorAdminAssistant,
          allowOverflow: true,
        },
      ],
    ];
  }

  return columns;
};

export default buildDirectoryColumns;
